import { useCallback, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as businessService from '../../../data-layer/business/service';
import { BusinessType } from '../../../data-layer/business/types';
import { showNotificationAsync } from '../../../data-layer/notifications/actions';
import { useDispatch } from '../../../data-layer/useDispatch';
import { setUserRecords } from '../../../data-layer/user/actions';
import { toBusinessGeoModel } from '../../../mappers/business/toBusinessPersistenceModel';
import { BusinessRegistrationStatus } from '../../../mappers/users/toUserModel';
import { BusinessRegistrationAccount } from '../../../ui/business/BusinessRegistrationAccount/BusinessRegistrationAccount';
import { BusinessRegistrationForm } from '../../../ui/business/BusinessRegistrationForm/BusinessRegistrationForm';
import { textDictionary } from '../../../ui/business/BusinessRegistrationForm/constants';
import { BusinessTypeTabs } from '../../../ui/business/BusinessTypeTabs/BusinessTypeTabs';
import { MessengerButton } from '../../../ui/business/MessengerWidget';
import { RegistrationStatus } from '../../../ui/business/RegistrationStatus';
import { BloggerRegistrationUI, BusinessRegistrationUI } from '../../../ui/business/types';
import { DashboardPage } from '../../../ui/dashboard/DashboardPage';
import { IconDeclinedFilled } from '../../../ui/icon/IconDeclinedFilled';
import { Box } from '../../../ui/layout/Box';
import { Flex } from '../../../ui/layout/Flex';
import { Typography } from '../../../ui/typography/Typography';
import { useCpaTrial } from '../../../ui/users/CpaTrialDisclaimer/hooks/useCpaTrial';
import { canRegisterBusiness } from '../../../utils/accessService';
import {
    trackFieldFilled,
    ymTrackChatClick,
    ymTrackSubmitRegistration,
} from '../../../utils/metrika';
import { share } from '../../../utils/share';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useTranslation } from '../../../utils/useTranslation';
import { useCurrentUser } from '../../users/useCurrentUser';
import { withAuthProtection } from '../../withAuthProtection';
import { useBusinessGeo } from '../hooks/useBusinessGeo';
import { MODERATION_RULES_LINK } from './constants';
import { getBusinessType } from './utils/getBusinessType';
import { getRegistrationStatus } from './utils/getRegistrationStatus';
import { getRegistrationStatusProps } from './utils/getRegistrationStatusProps';
import { getVerificationStatus } from './utils/getVerificationStatus';

export const BusinessRegistration = withAuthProtection(() => {
    const t = useTranslation();
    const dispatch = useDispatch();
    const currentUser = useCurrentUser();
    const [query, setQuery] = useSearchParams();

    const enableServiceRegistration =
        Number(useExperimentFlag('enable_service_registration')) === 1;

    const [enableChangeType, setEnableChangeType] = useState(true);
    const [businessTypeExp, setBusinessTypeExp] = useState<BusinessType | undefined>();

    const onChangeBusinessType = useCallback((value: BusinessType) => {
        setBusinessTypeExp(value);
        setEnableChangeType(false);
    }, []);

    const onChangeTypeClick = useCallback(() => {
        setEnableChangeType(true);
    }, []);

    const businessType = getBusinessType({ query, currentUser, businessTypeExp });
    const isBlogger = businessType === BusinessType.BLOGGER;
    const registrationStatus = getRegistrationStatus(businessType, currentUser);
    const { isNotRegistered, refreshUserProfile } = useCpaTrial();

    const textMap = textDictionary[businessType];

    const enableBusinessGeo = Number(useExperimentFlag('enable_business_geo')) === 1 && !isBlogger;
    const enableBloggerGeo = Number(useExperimentFlag('enable_blogger_geo')) === 1 && isBlogger;
    const enableGeo = enableBusinessGeo || enableBloggerGeo;
    const {
        selectedRegions,
        regionQuery,
        geoSuggests,
        errorMessage,
        onSelectRegion,
        onRemoveRegion,
        onInputRegion,
    } = useBusinessGeo(undefined, { syncDefaultGeo: true });

    const onChangeTab = (type: BusinessType) => {
        setQuery({ type }, { replace: true });
    };

    const onSubmit = useCallback(
        async (value: BusinessRegistrationUI | BloggerRegistrationUI) => {
            try {
                let registrationTgCode: string | undefined;
                const geotargeting = enableGeo ? toBusinessGeoModel(selectedRegions) : undefined;

                if (enableServiceRegistration) {
                    registrationTgCode = await businessService.registerBusiness({
                        ...(value as BusinessRegistrationUI),
                        type: businessType,
                        needCpaRegistration: isBlogger ? isNotRegistered : undefined,
                    });
                } else if (isBlogger) {
                    registrationTgCode = await businessService.registerBlogger({
                        ...value,
                        needCpaRegistration: isNotRegistered,
                        geotargeting,
                    } as BusinessRegistrationUI);
                    refreshUserProfile();
                } else {
                    registrationTgCode = await businessService.registerBusiness({
                        ...(value as BusinessRegistrationUI),
                        geotargeting,
                    });
                }

                if (currentUser) {
                    dispatch(
                        setUserRecords({
                            ...currentUser,
                            ...getVerificationStatus({
                                businessType,
                                currentUser,
                                value,
                                registrationTgCode,
                            }),
                        }),
                    );
                }
                ymTrackSubmitRegistration({
                    userLogin: currentUser?.login,
                    page: textMap.logPage,
                });

                dispatch(showNotificationAsync(t(textMap.submitSuccessText)));
            } catch (_error) {
                dispatch(showNotificationAsync(t(textMap.submitFailedText)));
            }
        },
        [
            enableGeo,
            selectedRegions,
            enableServiceRegistration,
            isBlogger,
            currentUser,
            textMap.logPage,
            textMap.submitSuccessText,
            textMap.submitFailedText,
            dispatch,
            t,
            businessType,
            isNotRegistered,
            refreshUserProfile,
        ],
    );

    const onCopy = useCallback(async () => {
        const tgCode = currentUser?.registrationTgCode;
        const notification = tgCode ? t('Код успешно скопирован') : t('Текст успешно скопирован');

        await share({
            text: tgCode ? tgCode : `${currentUser?.login} ${textMap.accountAccessVerification}`,
        });

        dispatch(showNotificationAsync(notification));
    }, [
        currentUser?.login,
        dispatch,
        t,
        textMap.accountAccessVerification,
        currentUser?.registrationTgCode,
    ]);

    const onFieldBlur = useCallback(
        (fieldName: string, value: string) => {
            if (value) {
                trackFieldFilled(fieldName, {
                    userLogin: currentUser?.login,
                    page: textMap.logPage,
                    value,
                });
            }
        },
        [currentUser?.login, textMap.logPage],
    );

    const onChatClick = useCallback(() => {
        ymTrackChatClick({
            page: 'Регистрация',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const enableRegister = canRegisterBusiness(registrationStatus);

    if (enableServiceRegistration && enableChangeType && enableRegister) {
        return (
            <BusinessRegistrationAccount type={businessTypeExp} onChange={onChangeBusinessType} />
        );
    }

    return (
        <DashboardPage mt={{ sm: 2 }}>
            <Box position="fixed" right={2} bottom={{ xs: 8, sm: 2 }}>
                <MessengerButton onClick={onChatClick} />
            </Box>
            {!enableServiceRegistration ? (
                <Flex
                    p={2}
                    mb={1}
                    width="100%"
                    height={{ xs: '100%', sm: 'auto' }}
                    borderRadius={{ sm: 2 }}
                    backgroundColor="var(--background-primary)"
                >
                    <BusinessTypeTabs businessType={businessType} onChange={onChangeTab} />
                </Flex>
            ) : null}
            {currentUser && enableRegister ? (
                <Flex>
                    {registrationStatus === BusinessRegistrationStatus.DECLINED && (
                        <Flex
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            p={2}
                            borderRadius={2}
                            backgroundColor="var(--background-primary)"
                            mb={1}
                            borderTopLeftRadius={
                                enableServiceRegistration ? { xs: 0, sm: 2 } : undefined
                            }
                            borderTopRightRadius={
                                enableServiceRegistration ? { xs: 0, sm: 2 } : undefined
                            }
                        >
                            <IconDeclinedFilled size={24} color="var(--color-danger)" />
                            <Flex>
                                <Typography pb={0.5}>
                                    {t(textMap.declinedAccountStatus)}
                                    <Typography
                                        display="inline"
                                        as={Link}
                                        to={MODERATION_RULES_LINK}
                                        color="link"
                                        textDecoration="none"
                                    >
                                        {t('Справке')}
                                    </Typography>
                                    .
                                </Typography>
                                <Typography>
                                    {t('Если возникли вопросы, вы всегда можете обратиться в ')}
                                    <Typography
                                        display="inline"
                                        as={Link}
                                        to={
                                            `${process.env.REACT_APP_BETA_SUPPORT_FORM_LINK}?login=${currentUser.login}` ||
                                            ''
                                        }
                                        color="link"
                                        textDecoration="none"
                                    >
                                        {t('поддержку')}.
                                    </Typography>
                                </Typography>
                            </Flex>
                        </Flex>
                    )}

                    <BusinessRegistrationForm
                        initialValues={{
                            domain: '',
                            displayName: '',
                            offerAgreement: false,
                            sendAdMails: true,
                            email: `${currentUser.login}@yandex.ru`,
                        }}
                        onSubmit={onSubmit}
                        onFieldBlur={onFieldBlur}
                        businessType={businessType}
                        onChangeTab={onChangeTab}
                        geo={
                            enableGeo
                                ? {
                                      value: regionQuery,
                                      selected: selectedRegions,
                                      suggests: geoSuggests,
                                      errorMessage,
                                      onSelect: onSelectRegion,
                                      onRemove: onRemoveRegion,
                                      onInput: onInputRegion,
                                  }
                                : undefined
                        }
                        onChangeTypeClick={onChangeTypeClick}
                        registrationStatus={registrationStatus}
                    />
                </Flex>
            ) : undefined}
            {currentUser && !enableRegister ? (
                <RegistrationStatus
                    {...getRegistrationStatusProps({
                        status: registrationStatus,
                        login: currentUser.login,
                        onCopy,
                        businessType,
                        registrationTgCode: currentUser.registrationTgCode,
                    })}
                />
            ) : null}
        </DashboardPage>
    );
});
